import React, {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {X, Receipt, ArrowDownCircle, ArrowUpCircle, Wallet} from "lucide-react";

// Détail du CA TTC (clic sur la carte « CHIFFRE D'AFFAIRES TTC ») : CA des
// ventes seules, mouvements de fonds (entrées / sorties de caisse), puis le
// total avec mouvements — celui affiché sur la carte. Les champs détaillés
// viennent de /activite/metrics ; avec une ancienne API on retombe sur
// revenue_with_tax - fund_movements_net.
const RevenueDetailsModal = ({stats, formatCurrency, onClose, isDarkMode = false}) => {
    const {t} = useTranslation("activite");

    const withMovements = Number(stats.revenue_with_tax || 0);
    const net = Number(stats.fund_movements_net || 0);
    const withoutMovements = stats.revenue_with_tax_without_movements != null
        ? Number(stats.revenue_with_tax_without_movements)
        : withMovements - net;
    const hasSplit = stats.fund_movements_in != null && stats.fund_movements_out != null;
    const movementsIn = hasSplit ? Number(stats.fund_movements_in) : Math.max(net, 0);
    const movementsOut = hasSplit ? Number(stats.fund_movements_out) : Math.min(net, 0);

    useEffect(() => {
        const handleKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", handleKey);
        return () => document.removeEventListener("keydown", handleKey);
    }, [onClose]);

    const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
    const panel = isDarkMode ? "bg-gray-700/60" : "bg-gray-50";
    const signed = (value) => (value > 0 ? "+" : value < 0 ? "−" : "") + formatCurrency(Math.abs(value));

    const movementRow = (Icon, color, label, value, count) => (
        <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
                <Icon className={`w-4 h-4 shrink-0 ${color}`}/>
                <span className="text-sm truncate">{label}</span>
                {count != null && (
                    <span className={`text-xs ${muted}`}>({t("statsCards.revenueDetails.movementsCount", {count})})</span>
                )}
            </div>
            <span className={`text-sm font-semibold ${color}`}>{signed(value)}</span>
        </div>
    );

    return (
        // Mobile : bottom sheet collée en bas ; à partir de md : modal centrée.
        <div
            className="fixed inset-0 z-50 flex items-end md:items-center justify-center md:p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="revenue-details-title"
        >
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm pointer-events-none animate-fade-in"/>

            <div className={`relative w-full md:max-w-md max-h-[90vh] flex flex-col rounded-t-2xl md:rounded-2xl shadow-2xl overflow-hidden animate-sheet-up md:animate-zoom-in ${isDarkMode ? "bg-gray-800 text-gray-100" : "bg-white text-gray-900"}`}>
                {/* Poignée de la bottom sheet (mobile) */}
                <div className="md:hidden flex justify-center pt-2.5 pb-1 bg-primary/10">
                    <div className={`w-10 h-1 rounded-full ${isDarkMode ? "bg-gray-600" : "bg-gray-300"}`}/>
                </div>
                <div className="flex items-center justify-between px-5 py-4 border-b border-primary/30 bg-primary/10">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                            <Receipt className="w-4 h-4 text-white"/>
                        </div>
                        <h3 id="revenue-details-title" className="text-sm font-semibold text-primary">
                            {t("statsCards.revenueDetails.title")}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label={t("statsCards.revenueDetails.close")}
                        className={`p-1.5 rounded-full transition-colors ${isDarkMode ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}
                    >
                        <X className="w-4 h-4"/>
                    </button>
                </div>

                <div className="p-5 space-y-4 overflow-y-auto pb-[calc(1.25rem+env(safe-area-inset-bottom))] md:pb-5">
                    {/* CA des ventes seules */}
                    <div className={`rounded-xl p-3.5 ${panel}`}>
                        <div className="flex items-baseline justify-between gap-3">
                            <p className="text-sm font-medium">{t("statsCards.revenueDetails.withoutMovements")}</p>
                            <p className="text-lg font-bold">{formatCurrency(withoutMovements)}</p>
                        </div>
                        <p className={`text-xs mt-1 ${muted}`}>{t("statsCards.revenueDetails.withoutMovementsDesc")}</p>
                    </div>

                    {/* Mouvements de fonds */}
                    <div className={`rounded-xl p-3.5 space-y-2 ${panel}`}>
                        <p className="text-sm font-medium">{t("statsCards.revenueDetails.movements")}</p>
                        {movementRow(ArrowDownCircle, "text-green-600 dark:text-green-400", t("statsCards.revenueDetails.movementsIn"), movementsIn, hasSplit ? stats.fund_movements_in_count : null)}
                        {movementRow(ArrowUpCircle, "text-red-600 dark:text-red-400", t("statsCards.revenueDetails.movementsOut"), movementsOut, hasSplit ? stats.fund_movements_out_count : null)}
                        <div className={`flex items-center justify-between pt-2 border-t ${isDarkMode ? "border-gray-600" : "border-gray-200"}`}>
                            <span className="text-sm">{t("statsCards.revenueDetails.movementsNet")}</span>
                            <span className="text-sm font-semibold">{signed(net)}</span>
                        </div>
                        <p className={`text-xs ${muted}`}>{t("statsCards.revenueDetails.movementsDesc")}</p>
                    </div>

                    {/* Total avec mouvements = valeur de la carte */}
                    <div className="rounded-xl p-3.5 bg-primary/10 border border-primary/30">
                        <div className="flex items-baseline justify-between gap-3">
                            <p className="text-sm font-medium flex items-center gap-2">
                                <Wallet className="w-4 h-4 text-primary"/>
                                {t("statsCards.revenueDetails.withMovements")}
                            </p>
                            <p className="text-lg font-bold text-primary">{formatCurrency(withMovements)}</p>
                        </div>
                        <p className={`text-xs mt-1 ${muted}`}>{t("statsCards.revenueDetails.withMovementsDesc")}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RevenueDetailsModal;
